import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import teammateGroupFlourishBottom from '../../../assets/images/teammateGroupFlourishBottom.svg';
import teammateGroupFlourishTop from '../../../assets/images/teammateGroupFlourishTop.svg';
import teammateGroupsVisual from '../../../assets/images/teammateGroupsVisual.svg';

interface OnboardingTeammateGroupVisualProps {
  hasSelectedTeamType: boolean;
  selection?: string;
  shouldAnimate: boolean;
}

interface StyledWithAnimationProps {
  $hasSelection: boolean;
  $shouldAnimate: boolean;
}

const StyledVisualContainerDiv = styled.div<StyledWithAnimationProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  gap: 12px;

  width: 235px;
  height: 246px;

  background: ${(p) => p.theme.greys.white};
  box-shadow: 0px 0px 20px rgba(0, 27, 56, 0.1);
  border-radius: 16px;

  ${(p) =>
    p.$hasSelection &&
    p.$shouldAnimate &&
    css`
      animation: pulse 1s ease-in-out 0.5s forwards;

      @keyframes pulse {
        0% {
          transform: scale(1);
        }
        25% {
          transform: scale(1.08);
        }
        50% {
          transform: scale(1.05);
        }
        75% {
          transform: scale(1.06);
        }
        100% {
          transform: scale(1.05);
        }
      }
    `}
`;

interface StyledPropsWithSelection {
  $hasSelection: boolean;
}

const StyledFlourishContainer = styled.div<StyledPropsWithSelection>`
  display: inline-flex;
  align-items: center;
  position: relative;

  &:before {
    content: '';
    display: inline-block;
    mask-image: url(${teammateGroupFlourishBottom});
    position: absolute;
    width: 36px;
    height: 32px;
    bottom: -44px;
    left: -35px;
    background-color: ${(p) => p.theme.greys.black};
    opacity: 0;
    ${(p) =>
      p.$hasSelection &&
      css`
        opacity: 1;
        transition: opacity 1s linear 0.5s;
      `}
  }
  &:after {
    content: '';
    display: inline-block;
    mask-image: url(${teammateGroupFlourishTop});
    position: absolute;
    width: 33px;
    height: 38px;
    right: -40px;
    top: -43px;
    background-color: ${(p) => p.theme.greys.black};
    opacity: 0;
    ${(p) =>
      p.$hasSelection &&
      css`
        opacity: 1;
        transition: opacity 1s linear 0.5s;
      `}
  }
`;

interface StyledTeamTextDivProps extends StyledWithAnimationProps {
  $selectionIsFilled: boolean;
}

const StyledTeamTextDiv = styled.div<StyledTeamTextDivProps>`
  font-family: 'Roobert', Helvetica, Arial, sans-serif;
  font-weight: ${fontWeights.bold};
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  width: 100%;
  ${ellipsis()}

  color: ${(p) => (p.$hasSelection && p.$selectionIsFilled ? p.theme.greys.black : p.theme.greys.shade50)};

  ${(p) =>
    p.$hasSelection &&
    p.$shouldAnimate &&
    css`
      animation: fadeIn 0.8s;
    `}

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

export const OnboardingTeammateGroupVisual: FC<OnboardingTeammateGroupVisualProps> = (props) => {
  const {hasSelectedTeamType, selection} = props;

  return (
    <StyledFlourishContainer $hasSelection={hasSelectedTeamType}>
      <StyledVisualContainerDiv $hasSelection={hasSelectedTeamType} $shouldAnimate={props.shouldAnimate}>
        <img src={teammateGroupsVisual} alt="teammategroups-visual" />
        <StyledTeamTextDiv
          $hasSelection={hasSelectedTeamType}
          $shouldAnimate={props.shouldAnimate}
          $selectionIsFilled={Boolean(selection)}
        >
          {selection || <Trans id="teammategroups-visual-default-text">Teammate</Trans>}
          <div>
            <Trans id="teammategroups-visual-group-text">group</Trans>
          </div>
        </StyledTeamTextDiv>
      </StyledVisualContainerDiv>
    </StyledFlourishContainer>
  );
};
